import React, { useEffect, useRef, useState } from 'react';
import { AdresseSaisie, adresseSaisieComplete, adresseUneLigne, ligneVoie, separerTypeVoie } from '../../utils/adresse';
import { SuggestionIgn, useRechercheAdresseIgn } from './useRechercheAdresseIgn';
import './ChampAdresse.css';

interface ChampAdresseProps {
  valeur: AdresseSaisie;
  onChange: (adresse: AdresseSaisie) => void;
  /** En France, la recherche IGN est la seule saisie ; ailleurs, les champs eclates. */
  enFrance: boolean;
  /** Erreurs serveur par champ (cles numero_voie, nom_voie, code_postal, ville). */
  erreurs?: Partial<Record<keyof AdresseSaisie, string>>;
  /** Prefixe des id, pour distinguer deux instances sur une meme page. */
  idPrefixe?: string;
}

const CHAMPS_ECLATES = ['numero_voie', 'nom_voie', 'code_postal', 'ville'] as const;

/**
 * Adresse d'un organisme ou d'une entreprise.
 *
 * En France, un seul champ de recherche IGN : l'utilisateur tape, choisit dans la liste, et les
 * quatre champs qui partent en base (numero, voie, code postal, ville) se remplissent sans etre
 * montres. Hors de France, le geocodeur ne connait pas le territoire : les quatre champs sont
 * la saisie.
 */
const ChampAdresse: React.FC<ChampAdresseProps> = ({
  valeur,
  onChange,
  enFrance,
  erreurs = {},
  idPrefixe = 'adresse',
}) => {
  const [recherche, setRecherche] = useState('');
  const [listeOuverte, setListeOuverte] = useState(false);
  const [rechercheQuittee, setRechercheQuittee] = useState(false);
  const rechercheRef = useRef<HTMLInputElement>(null);
  const { suggestions, chargement, rechercher, vider } = useRechercheAdresseIgn();

  const complete = adresseSaisieComplete(valeur);

  // Le champ « Voie » affiche type + nom reunis et les re-separe a chaque frappe. Un etat local
  // garde le texte tel que tape : recompose depuis la valeur eclatee, il perdrait l'espace en fin
  // de saisie et l'utilisateur ne pourrait plus taper « Rue des ».
  const [voieTexte, setVoieTexte] = useState(() => ligneVoie(null, valeur.type_voie, valeur.nom_voie));
  useEffect(() => {
    const composee = ligneVoie(null, valeur.type_voie, valeur.nom_voie);
    setVoieTexte((courant) => (courant.trim() === composee ? courant : composee));
  }, [valeur.type_voie, valeur.nom_voie]);

  // Une adresse complete posee par le code (SIRENE, choix dans la liste) se reflete dans le champ
  // de recherche sans relancer de recherche.
  useEffect(() => {
    if (!complete) return;
    setRecherche(adresseUneLigne(
      ligneVoie(valeur.numero_voie, valeur.type_voie, valeur.nom_voie),
      valeur.code_postal,
      valeur.ville,
    ));
    setRechercheQuittee(false);
  }, [complete, valeur.numero_voie, valeur.type_voie, valeur.nom_voie, valeur.code_postal, valeur.ville]);

  // Le champ de recherche porte l'obligation : il reste invalide (bouton grise, liste des
  // manques, bulle du navigateur) jusqu'a ce qu'une adresse ait ete choisie dans la liste ou
  // posee par SIRENE.
  useEffect(() => {
    rechercheRef.current?.setCustomValidity(complete ? '' : 'Choisissez votre adresse dans la liste.');
  }, [complete]);

  const majChamp = (champ: keyof AdresseSaisie) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...valeur, [champ]: e.target.value });

  const majVoie = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVoieTexte(e.target.value);
    const { typeVoie, nomVoie } = separerTypeVoie(e.target.value);
    // « Rue » seul remplit le champ visible mais laisse le nom de voie vide en base : le
    // navigateur doit le voir comme manquant.
    e.target.setCustomValidity(
      nomVoie === '' && e.target.value.trim() !== '' ? 'Indiquez le nom de la voie, par exemple « Rue de la République ».' : '',
    );
    onChange({ ...valeur, type_voie: typeVoie, nom_voie: nomVoie });
  };

  const choisir = (suggestion: SuggestionIgn) => {
    const { typeVoie, nomVoie } = separerTypeVoie(suggestion.rue);
    onChange({
      numero_voie: suggestion.numero,
      type_voie: typeVoie,
      nom_voie: nomVoie,
      code_postal: suggestion.codePostal,
      ville: suggestion.ville,
    });
    vider();
    setListeOuverte(false);
  };

  if (!enFrance) {
    return (
      <div className="champ-adresse champ-adresse-champs">
        <div className="form-group champ-adresse-numero">
          <label htmlFor={`${idPrefixe}-numero`}>Numéro</label>
          <input
            type="text"
            id={`${idPrefixe}-numero`}
            name="numero_voie"
            value={valeur.numero_voie}
            onChange={majChamp('numero_voie')}
            placeholder="Ex : 12"
            maxLength={10}
            className={erreurs.numero_voie ? 'input-error' : ''}
          />
          {erreurs.numero_voie && <span className="error-message">{erreurs.numero_voie}</span>}
        </div>
        <div className="form-group champ-adresse-voie">
          <label htmlFor={`${idPrefixe}-voie`}>Rue / voie<span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            id={`${idPrefixe}-voie`}
            name="nom_voie"
            value={voieTexte}
            onChange={majVoie}
            placeholder="Ex : Rue de la Loi, Hauptstrasse"
            maxLength={255}
            required
            className={erreurs.nom_voie ? 'input-error' : ''}
          />
          {erreurs.nom_voie && <span className="error-message">{erreurs.nom_voie}</span>}
        </div>
        <div className="form-group champ-adresse-code-postal">
          <label htmlFor={`${idPrefixe}-code-postal`}>Code postal<span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            id={`${idPrefixe}-code-postal`}
            name="code_postal"
            value={valeur.code_postal}
            onChange={majChamp('code_postal')}
            placeholder="Ex : 1000, 1012 AB, 00-950"
            maxLength={12}
            required
            className={erreurs.code_postal ? 'input-error' : ''}
          />
          {erreurs.code_postal && <span className="error-message">{erreurs.code_postal}</span>}
        </div>
        <div className="form-group champ-adresse-ville">
          <label htmlFor={`${idPrefixe}-ville`}>Ville<span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            id={`${idPrefixe}-ville`}
            name="ville"
            value={valeur.ville}
            onChange={majChamp('ville')}
            placeholder="Ex : Bruxelles"
            maxLength={100}
            required
            className={erreurs.ville ? 'input-error' : ''}
          />
          {erreurs.ville && <span className="error-message">{erreurs.ville}</span>}
        </div>
      </div>
    );
  }

  const aideSelection = rechercheQuittee && recherche.trim() !== '' && !complete;
  // Le serveur renvoie ses erreurs par champ eclate ; en France seul le champ de recherche est
  // visible, il porte donc la premiere d'entre elles.
  const erreurAdresse = CHAMPS_ECLATES.map((champ) => erreurs[champ]).find(Boolean);

  return (
    <div className="champ-adresse">
      <div className="form-group">
        <label htmlFor={`${idPrefixe}-recherche`}>Adresse<span style={{ color: 'red' }}>*</span></label>
        <div className="champ-adresse-recherche">
          <input
            ref={rechercheRef}
            type="text"
            id={`${idPrefixe}-recherche`}
            value={recherche}
            onChange={(e) => {
              setRecherche(e.target.value);
              setRechercheQuittee(false);
              setListeOuverte(true);
              rechercher(e.target.value);
            }}
            onFocus={() => setListeOuverte(true)}
            onBlur={() => {
              setListeOuverte(false);
              setRechercheQuittee(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
              if (e.key === 'Escape') setListeOuverte(false);
            }}
            placeholder="Tapez l'adresse puis choisissez-la dans la liste"
            autoComplete="off"
            required
            className={erreurAdresse ? 'input-error' : ''}
          />
          {chargement && <span className="champ-adresse-spinner" aria-hidden="true" />}
          {listeOuverte && suggestions.length > 0 && (
            <ul className="champ-adresse-liste" role="listbox">
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choisir(suggestion);
                  }}
                >
                  {suggestion.libelle}
                </li>
              ))}
            </ul>
          )}
        </div>
        {aideSelection && (
          <p className="champ-adresse-alerte">Choisissez votre adresse dans la liste qui s'affiche sous le champ.</p>
        )}
        {erreurAdresse && <span className="error-message">{erreurAdresse}</span>}
      </div>
    </div>
  );
};

export default ChampAdresse;
